import { TextColor, TextScale } from 'particles/Text'
import React, { useState } from 'react'
import { useWindowSize } from 'hooks'
import { useNewTranslation } from 'hooks/index'
import RectangleButton, { RectangleButtonProps } from 'particles/Button/RectangleButton'
import IconButton from 'particles/Button/IconButton'
import RichContentMenu from 'particles/Menu/RichContentMenu'
import MenuItem from 'particles/MenuItem'
import { IconName } from 'particles/Icon'

interface ItemLabelProps {
    color?: TextColor
    scale?: TextScale
}

export interface PopoverItemProps {
    itemPrimaryLabel: string
    itemSecondaryLabel?: string
    onClick: (e: React.MouseEvent<HTMLElement>) => void
    show: boolean
    disabled?: boolean
    itemPrimaryLabelProps?: ItemLabelProps
    itemSecondaryLabelProps?: ItemLabelProps
    icon: IconName
}

interface PopoverButtonProps {
    popoverActions?: PopoverItemProps[]
    popoverTitleText: string
    buttonProps?: Partial<RectangleButtonProps>
    isVerticalMenu?: boolean
    children?: (closePopup: () => void) => React.ReactNode
}

const PopoverButton: React.FC<PopoverButtonProps> = ({
    popoverTitleText,
    popoverActions,
    buttonProps,
    isVerticalMenu = true,
    children,
}) => {
    const [newT] = useNewTranslation()

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

    const windowSize = useWindowSize()

    const visibleActions = popoverActions && popoverActions.filter(({ show }) => show)

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        e.preventDefault()
        setAnchorEl(e.currentTarget)
    }

    const closePopup = (): void => {
        setAnchorEl(null)
    }

    return (
        <>
            {windowSize && windowSize > 768 ? (
                <RectangleButton
                    form="outlined"
                    size="s"
                    label={newT('button.selectAction')}
                    onClick={handleButtonClick}
                    {...buttonProps}
                />
            ) : (
                <IconButton form="outlined" onClick={handleButtonClick} size="l" icon="dotsThree" />
            )}
            <RichContentMenu
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={closePopup}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                sx={{ marginTop: '-10px' }}
                isVertical={isVerticalMenu}
            >
                {children ? (
                    children(closePopup)
                ) : (
                    <>
                        <MenuItem.Title text={popoverTitleText} case="uppercase" />
                        {visibleActions?.map((option, index) => (
                            <div key={index} className="menu-section">
                                <MenuItem.Text
                                    icon={option.icon}
                                    key={`popoverButton-${index}`}
                                    text={option.itemPrimaryLabel}
                                    description={option.itemSecondaryLabel}
                                    onClick={e => {
                                        closePopup()
                                        option.onClick(e)
                                    }}
                                    isDisabled={option.disabled}
                                    tabIndex={index}
                                    isRichContentItem
                                />
                            </div>
                        ))}
                    </>
                )}
            </RichContentMenu>
        </>
    )
}

export default PopoverButton
